// =========================================================================
// timeline-grouping — bucket timeline items into day groups for the feed
// ("Today" / "Yesterday" / "This week" / "MMM d, yyyy"). Pure + deterministic
// (takes an injectable `now` so it can be unit-tested without mocking the clock)
// so a rep can scan the activity feed by recency at a glance.
// =========================================================================

import { differenceInCalendarDays, format } from "date-fns";
import type { TimelineItem } from "../hooks/timeline-types";

export interface TimelineDayGroup {
  /** Stable key for React lists (the calendar day, yyyy-MM-dd). */
  key: string;
  /** Display label for the group header. */
  label: string;
  items: TimelineItem[];
}

function groupLabel(date: Date, now: Date): string {
  const days = differenceInCalendarDays(now, date);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7) return "This week";
  return format(date, "MMM d, yyyy");
}

/**
 * Group items by calendar day, preserving their incoming order (the fetcher
 * already sorts newest-first). Items with an unparseable `occurred_at` fall into
 * an "Earlier" bucket rather than being dropped.
 */
export function groupTimelineByDay(
  items: TimelineItem[],
  now: Date = new Date(),
): TimelineDayGroup[] {
  const groups: TimelineDayGroup[] = [];
  const indexByKey = new Map<string, number>();

  for (const item of items) {
    const date = new Date(item.occurred_at);
    const valid = !Number.isNaN(date.getTime());
    const key = valid ? format(date, "yyyy-MM-dd") : "earlier";
    const label = valid ? groupLabel(date, now) : "Earlier";

    let idx = indexByKey.get(key);
    if (idx === undefined) {
      idx = groups.length;
      indexByKey.set(key, idx);
      groups.push({ key, label, items: [] });
    }
    groups[idx].items.push(item);
  }

  return groups;
}
